import { useState } from "react";
import { EmptyState, ErrorState, MktButton, SectionHeader, StatePill, Stat, Tag } from "./primitives";
import { fmtMicro, shortAddr } from "./mkt-format";
import { useT } from "../../i18n";
import type { OwnerAudit, OwnerSupplyCheck, PrivateSale } from "../../lib/marketplace-api";

const PS_STATUS_LABEL: Record<string, string> = {
  active: "marketplace.owner.psStatusActive",
  completed: "marketplace.owner.psStatusCompleted",
  expired: "marketplace.owner.psStatusExpired",
  cancelled: "marketplace.owner.psStatusCancelled",
};

export function OwnerAuditPanel({
  privateSales,
  cancelSupported,
  onRequestCancel,
  onLookup,
  supplyResult,
  supplyLoading,
  supplyError,
}: {
  privateSales: OwnerAudit["private_sales"];
  cancelSupported: boolean;
  onRequestCancel: (sale: PrivateSale) => void;
  onLookup: (owner: string) => void;
  supplyResult?: OwnerSupplyCheck;
  supplyLoading?: boolean;
  supplyError?: string | null;
}) {
  const t = useT();
  const [owner, setOwner] = useState("");
  return (
    <section data-testid="owner-audit-panel" className="space-y-10">
      <div>
        <SectionHeader
          index="03"
          title={t("marketplace.owner.privateSalesTitle")}
          action={
            <span className="font-mono text-[10.5px] uppercase tracking-[0.14em] text-text-muted">
              {privateSales.length}
            </span>
          }
        />
        {privateSales.length === 0 ? (
          <EmptyState
            testId="owner-private-sales-empty"
            title={t("marketplace.owner.noPrivateSalesTitle")}
            hint={t("marketplace.owner.noPrivateSalesHint")}
          />
        ) : (
          <div className="neon-glass rounded-lg border border-border-divider divide-y divide-border-divider">
            {privateSales.map((p) => (
              <div
                key={p.sale_id}
                data-testid="owner-private-sale-row"
                data-status={p.status}
                className="flex items-center justify-between gap-3 px-4 py-3"
              >
                <div className="flex flex-col">
                  <span className="font-display text-sm text-text-primary">{shortAddr(p.sale_id)}</span>
                  <span className="font-mono text-[10px] text-text-muted">{t("marketplace.owner.buyer", { addr: shortAddr(p.buyer) })}</span>
                  {p.expiry_real_ts ? (
                    <span data-testid="owner-ps-expiry" className="font-mono text-[9.5px] text-text-faint">
                      {new Date(p.expiry_real_ts * 1000).toISOString().slice(0, 16).replace("T", " ")}Z
                    </span>
                  ) : null}
                </div>
                <div className="flex items-center gap-4">
                  <span className="font-mono text-[11px] text-neon-teal">
                    {fmtMicro(p.unit_price_micro)} <span className="text-text-muted">{t("marketplace.owner.perSlot")}</span>
                  </span>
                  <StatePill
                    state={p.status === "completed" ? "sold" : p.status === "active" ? "sale_pending" : "cancelled"}
                    label={t(PS_STATUS_LABEL[p.status] ?? "marketplace.owner.psStatusActive")}
                    testId="owner-private-sale-state"
                  />
                  {p.cancellable ? (
                    <MktButton
                      variant="ghost"
                      testId="owner-ps-cancel-btn"
                      onClick={() => onRequestCancel(p)}
                    >
                      {t("marketplace.owner.cancelPrivateSale")}
                    </MktButton>
                  ) : (
                    <span data-testid="owner-ps-cancel-blocked" className="font-mono text-[10px] text-text-faint">
                      {t("marketplace.owner.cancelBlocked")}
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
        {!cancelSupported && privateSales.some((p) => p.cancellable) && (
          <p data-testid="owner-ps-cancel-unsupported-note" className="mt-3 font-mono text-[10px] text-neon-coral/80">
            {t("marketplace.owner.cancelUnsupportedNote")}
          </p>
        )}
      </div>

      <div>
        <SectionHeader index="04" title={t("marketplace.owner.supplyCheckTitle")} />
        <div className="neon-glass rounded-xl border border-border-divider p-5 space-y-4">
          <div className="flex flex-col sm:flex-row gap-3">
            <input
              data-testid="owner-supply-check-input"
              value={owner}
              onChange={(e) => setOwner(e.target.value)}
              placeholder={t("marketplace.owner.walletPlaceholder")}
              className="flex-1 h-10 px-3 rounded-md bg-elevated/60 border border-border-divider font-mono text-[12px] text-text-primary placeholder:text-text-faint focus:outline-none focus:border-neon-teal/50"
            />
            <MktButton
              testId="owner-supply-check-btn"
              onClick={() => onLookup(owner)}
              disabled={!owner.trim() || !!supplyLoading}
            >
              {supplyLoading ? t("marketplace.owner.checking") : t("marketplace.owner.auditWallet")}
            </MktButton>
          </div>
          {supplyError && <ErrorState testId="owner-supply-check-error" title={t("marketplace.owner.supplyCheckFailed")} detail={supplyError} />}
          {supplyResult && !supplyError && (
            <div data-testid="owner-supply-check-result" className="pt-2 space-y-4">
              <div className="flex items-center gap-6">
                <Stat label={t("marketplace.owner.owned")} testId="owner-supply-check-owned">
                  {supplyResult.owned}
                </Stat>
                <Stat label={t("marketplace.owner.paidOrigin")} testId="owner-supply-check-paid">
                  {supplyResult.with_paid_origin}
                </Stat>
                <div className="flex flex-col gap-1">
                  <span className="font-mono text-[9.5px] tracking-[0.2em] uppercase text-text-muted">{t("marketplace.owner.integrity")}</span>
                  <Tag
                    tone={supplyResult.consistent ? "teal" : "coral"}
                    testId="owner-supply-check-consistent"
                  >
                    {supplyResult.consistent ? t("marketplace.owner.consistent") : t("marketplace.owner.anomalies")}
                  </Tag>
                </div>
              </div>
              {supplyResult.anomalies.length > 0 && (
                <div className="divide-y divide-border-divider">
                  {supplyResult.anomalies.map((a, i) => (
                    <div
                      key={`${String(a.parcel_id)}-${i}`}
                      data-testid="owner-supply-anomaly-row"
                      className="flex items-center justify-between py-2 font-mono text-[11px]"
                    >
                      <span className="text-text-primary">{t("marketplace.common.parcelNumber", { id: String(a.parcel_id) })}</span>
                      <span className="text-neon-coral">{a.reason}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
